import { signOut } from 'aws-amplify/auth';
import { Context } from 'Context/Context'
import React, { useContext } from 'react'
import ReactModal from 'react-modal'
import { Briefcase, ShieldCheck, LogOut, User, BarChart3, Mail, Lock, HelpCircle, Crown, BadgeCheck, LayoutDashboard } from "lucide-react";
import {ADMIN, PROFESSIONAL, CLIENT, FIRM} from "Constants";

import { useNavigate } from 'react-router-dom';
import useIsMobile from 'Hooks/useIsMobile';

function MiniAvatarModal({uploadNew, onClose, doNotClose = true, onBack, buttonName = "yes", onSubmit, text}) {
    const {userDetails, setUserDetails, setSelectedMenuItem} = useContext(Context);
    const isMobile = useIsMobile();
    const navigate = useNavigate();
     const signOutFunc = async() => {
          try {
              setSelectedMenuItem(1);
              const signout = await signOut();
              localStorage.clear();
              setUserDetails({});

              navigate("/");
          }
          catch(error)
          {
            console.log(error);
          }

        }

    const isClient = userDetails?.role === CLIENT;

    // Client account menu, styled to match the reference "initials avatar"
    // dropdown design. "Billing & payments" from that reference is left out
    // here on purpose - clients aren't billed anywhere on this platform
    // (only professional/firm accounts carry a paid subscription), so
    // there's no page for a client version of that item to point to.
    // "My cases" and "Account & security" both map onto real client routes
    // (/client/cases, and the client profile panel - which already covers
    // profile details, email settings, password & security, and delete
    // account - see clientProfilePanelItems in Constants.js).
    const clientMenuItems = [
      { label: "My cases", icon: Briefcase, onClick: () => { onClose && onClose(); navigate("/client/cases"); } },
      { label: "Account & security", icon: ShieldCheck, onClick: () => { onClose && onClose(); navigate("/client/profile"); } },
    ];

    // Attorney / firm / admin account menu (reference "initials avatar" design).
    // Shortcuts go straight to the matching profile tab via /attorney/profile/:setPanel
    // (0 Profile, 6 Statistics, 2 Email settings, 1 Password & security, 7 Subscription).
    // Delete account / Team management stay in the profile side panel only - a quick
    // menu doesn't need the rarely used or destructive options.
    const isAdmin = userDetails?.role === ADMIN;
    const isAssistant = !!userDetails?.isUserAssistant;
    const isFirm = userDetails?.role === FIRM;
    const initials = ((userDetails.firstName?.[0] || "") + (userDetails.lastName?.[0] || "")).toUpperCase();
    const hasPic = userDetails?.profilePic && userDetails.profilePic !== "";
    const goPanel = (key) => { onClose && onClose(); navigate("/attorney/profile/" + key); };
    const showStats = !isAdmin && !isAssistant && (isFirm || userDetails?.verified);
    const showPremiumRow = !isAdmin && !isAssistant;
    // Dashboard link first, so the portal is reachable from pages without the app
    // header (e.g. the homepage on a phone). Same landing pages as sign-in.
    const go = (path) => { onClose && onClose(); navigate(path); };
    const proMenuItems = isAdmin ? [
      { label: "Dashboard", icon: LayoutDashboard, onClick: () => go(userDetails?.isAdminAssistant ? "/admin/approve" : "/admin/outreach") },
      { label: "My profile", icon: User, onClick: () => go("/admin/profile") },
    ] : [
      { label: "Case dashboard", icon: LayoutDashboard, onClick: () => go("/attorney/casesFeed/availableCases") },
      { label: isFirm ? "Firm profile" : "Profile", icon: User, onClick: () => goPanel(0) },
      ...(showStats ? [{ label: "Statistics", icon: BarChart3, onClick: () => goPanel(6) }] : []),
      { label: "Email settings", icon: Mail, onClick: () => goPanel(2) },
      { label: "Password & security", icon: Lock, onClick: () => goPanel(1) },
      { label: "Help & support", icon: HelpCircle, onClick: () => { onClose && onClose(); navigate("/contact-us"); } },
    ];
    const itemHover = {
      onMouseEnter: (e) => e.currentTarget.style.background = 'rgba(20,33,61,.04)',
      onMouseLeave: (e) => e.currentTarget.style.background = 'transparent',
    };

  return (
    <ReactModal
    shouldCloseOnEsc = {doNotClose}
    shouldCloseOnOverlayClick = {doNotClose}
    onRequestClose={onClose}
    isOpen = {uploadNew}
    style={{
        overlay: {
          zIndex: 50,
          backgroundColor: 'rgba(255, 255, 255, 0)',
        },
        content: {
            zIndex: 50,
            height: 'max-content',
            width: 'auto',
            top: isMobile ? '55px' : '70px',
            padding: '0px',
            right: isMobile ? '8px' : '20px',
            left: 'auto',
            bottom: 'auto',
            border: 'none',
            background: 'transparent',
            borderRadius: '0px',
            overflow: 'visible',
        }
      }}
    >
      {isClient ? (
        <div
          className="font-plexsans overflow-hidden"
          style={{width:'240px', background:'#fffdf8', borderRadius:'10px', border:'1px solid rgba(20,33,61,.1)', boxShadow:'0 8px 28px rgba(20,33,61,.14)'}}
          role="menu" aria-orientation="vertical" aria-labelledby="menu-button"
        >
          <div className="flex items-center gap-3 px-4 py-3.5" style={{borderBottom:'1px solid rgba(20,33,61,.08)'}}>
            <div className="flex items-center justify-center flex-none rounded-full" style={{width:'36px', height:'36px', background:'#14213d', color:'#c9a26a', font:"600 13px/1 'IBM Plex Sans',sans-serif"}}>
              {(userDetails.firstName?.[0] || "").toUpperCase()}{(userDetails.lastName?.[0] || "").toUpperCase()}
            </div>
            <div className="min-w-0">
              <div className="truncate" style={{font:"600 13px/1.3 'IBM Plex Sans',sans-serif", color:'#14213d'}}>
                {userDetails.firstName} {userDetails.lastName}
              </div>
              <div className="truncate" style={{font:"400 11px/1.4 'IBM Plex Sans',sans-serif", color:'rgba(20,33,61,.5)'}}>
                {userDetails.email}
              </div>
            </div>
          </div>

          <div className="py-1.5">
            {clientMenuItems.map(({label, icon: Icon, onClick}) => (
              <div
                key={label}
                onClick={onClick}
                role="menuitem" tabIndex="-1"
                className="flex items-center gap-2.5 px-4 py-2.5 cursor-pointer transition-colors"
                style={{font:"500 13px/1 'IBM Plex Sans',sans-serif", color:'#14213d'}}
                onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(20,33,61,.04)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <Icon size={16} color="#8a6231" />
                {label}
              </div>
            ))}
          </div>

          <div style={{borderTop:'1px solid rgba(20,33,61,.08)'}} className="py-1.5">
            <div
              onClick={() => signOutFunc()}
              role="menuitem" tabIndex="-1"
              className="flex items-center gap-2.5 px-4 py-2.5 cursor-pointer transition-colors"
              style={{font:"500 13px/1 'IBM Plex Sans',sans-serif", color:'#b3261e'}}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(179,38,30,.06)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <LogOut size={16} color="#b3261e" />
              Sign out
            </div>
          </div>
        </div>
 ) : (
        <div
          className="font-plexsans overflow-hidden"
          style={{width: isMobile ? '88vw' : '320px', maxWidth:'320px', background:'#fffdf8', borderRadius:'10px', border:'1px solid rgba(20,33,61,.1)', boxShadow:'0 8px 28px rgba(20,33,61,.14)'}}
          role="menu" aria-orientation="vertical" aria-labelledby="menu-button"
        >
          <div className="flex items-center gap-3.5" style={{padding:'18px 20px', borderBottom:'1px solid rgba(20,33,61,.08)'}}>
            <div className="flex items-center justify-center flex-none rounded-full overflow-hidden" style={{width:'44px', height:'44px', background:'#c9a26a', color:'#14213d', font:"600 15px/1 'IBM Plex Sans',sans-serif"}}>
              {hasPic ? <img src={userDetails.profilePic} alt="" className="w-full h-full object-cover"/> : initials}
            </div>
            <div className="min-w-0">
              <div className="truncate" style={{font:"600 15px/1.3 'IBM Plex Sans',sans-serif", color:'#14213d'}}>
                {userDetails.firstName} {userDetails.lastName}
              </div>
              {userDetails?.company &&
                <div className="flex items-center gap-1.5 truncate" style={{font:"500 12.5px/1.4 'IBM Plex Sans',sans-serif", color:'#14213d', marginTop:'2px'}}>
                  {userDetails?.verified && <BadgeCheck size={14} color="#fffdf8" fill="#8a6231" className="flex-none"/>}
                  <span className="truncate">{userDetails.company}</span>
                </div>
              }
              <div className="truncate" style={{font:"400 11.5px/1.4 'IBM Plex Sans',sans-serif", color:'rgba(20,33,61,.5)', marginTop:'2px'}}>
                {userDetails.email}
              </div>
            </div>
          </div>

          {showPremiumRow &&
            <div style={{padding:'14px 16px 4px'}}>
              {userDetails?.isActiveSubscription ?
                <div className="flex items-center gap-2" style={{padding:'10px 14px', borderRadius:'6px', background:'rgba(138,98,49,.07)', border:'1px solid rgba(138,98,49,.3)'}}>
                  <Crown size={14} fill="#8a6231" strokeWidth={0} className="flex-none"/>
                  <span style={{font:"600 13px/1 'IBM Plex Sans',sans-serif", color:'#8a6231'}}>Premium member</span>
                  <span onClick={() => goPanel(7)} className="cursor-pointer hover:text-tanDark" style={{marginLeft:'auto', font:"500 12.5px/1 'IBM Plex Sans',sans-serif", color:'#8a6231', textDecoration:'underline'}}>Manage</span>
                </div>
                :
                <div onClick={() => { onClose && onClose(); navigate("/attorney/subscription"); }} className="flex items-center gap-2 cursor-pointer transition-colors hover:bg-[rgba(138,98,49,0.1)]" style={{padding:'10px 14px', borderRadius:'6px', background:'rgba(138,98,49,.05)', border:'1px solid rgba(138,98,49,.3)'}}>
                  <Crown size={14} fill="#8a6231" strokeWidth={0} className="flex-none"/>
                  <span style={{font:"600 13px/1 'IBM Plex Sans',sans-serif", color:'#8a6231'}}>Upgrade to Premium</span>
                </div>
              }
            </div>
          }

          <div style={{padding:'6px 0'}}>
            {proMenuItems.map(({label, icon: Icon, onClick}) => (
              <div
                key={label}
                onClick={onClick}
                role="menuitem" tabIndex="-1"
                className="flex items-center gap-3 cursor-pointer transition-colors"
                style={{padding:'11px 22px', font:"500 13.5px/1 'IBM Plex Sans',sans-serif", color:'#14213d'}}
                {...itemHover}
              >
                <Icon size={16} strokeWidth={1.7} color="rgba(20,33,61,.55)" />
                {label}
              </div>
            ))}
          </div>

          <div style={{borderTop:'1px solid rgba(20,33,61,.08)', padding:'6px 0'}}>
            <div
              onClick={() => signOutFunc()}
              role="menuitem" tabIndex="-1"
              className="flex items-center gap-3 cursor-pointer transition-colors"
              style={{padding:'11px 22px', font:"500 13.5px/1 'IBM Plex Sans',sans-serif", color:'#9a3b2e'}}
              onMouseEnter={(e) => e.currentTarget.style.background = 'rgba(154,59,46,.06)'}
              onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
            >
              <LogOut size={16} strokeWidth={1.7} color="#9a3b2e" />
              Sign out
            </div>
          </div>
        </div>
      )}
    </ReactModal>
  )
}

export default MiniAvatarModal